import { useQuery } from '@tanstack/react-query';
import { makeBookingManagerApi } from 'services/BookingManagerApi/BookingManagerApi';
import {
  IBankTransactionImportResponse,
  IImportLogFilters,
  EBankTransactionImportStatus,
  TImportLogSortKey,
} from '../types';
import { ESortOrder } from 'store/common/types';

// Query key constant for cleanup
export const IMPORT_LOG_QUERY_KEY = 'importLog';

/**
 * Default/empty filter state for import log
 */
export const defaultImportLogFilters: IImportLogFilters = {
  startDate: null,
  endDate: null,
  statuses: [],
};

/**
 * Response type for import log list
 */
export interface IImportLogListResponse {
  data: IBankTransactionImportResponse[];
  meta: {
    total: number;
    page: number;
    perPage: number;
  };
}

/**
 * Hook for fetching import logs with pagination, sorting, and filtering.
 * OWA-10233
 */
export const useImportLogQuery = (
  page: number, // 1-indexed (matches Pagination component)
  perPage: number,
  sortBy: TImportLogSortKey,
  sortOrder: ESortOrder,
  filters: IImportLogFilters = defaultImportLogFilters
) => {
  return useQuery<IImportLogListResponse>({
    queryKey: [
      IMPORT_LOG_QUERY_KEY,
      page,
      perPage,
      sortBy,
      sortOrder,
      filters.startDate,
      filters.endDate,
      filters.statuses,
    ],
    queryFn: async () => {
      const api = makeBookingManagerApi();

      // Calculate offset from page (1-indexed)
      const offset = (page - 1) * perPage;

      // Build the filter object for the API
      const apiFilter: {
        createdAtFrom?: string;
        createdAtTo?: string;
        statuses?: EBankTransactionImportStatus[];
      } = {};

      if (filters.startDate) {
        apiFilter.createdAtFrom = filters.startDate;
      }
      if (filters.endDate) {
        apiFilter.createdAtTo = filters.endDate;
      }
      if (filters.statuses.length > 0) {
        apiFilter.statuses = filters.statuses;
      }

      // Make the API call to get the list
      const response = await api.searchImportLogs({
        filter: Object.keys(apiFilter).length > 0 ? apiFilter : undefined,
        pagination: {
          offset,
          limit: perPage,
        },
        sort: {
          key: sortBy,
          order: sortOrder === ESortOrder.ASC ? 'asc' : 'desc',
        },
      });

      const imports = response.data.imports;
      const total = response.data.count;

      // Transform to the format expected by the UI
      return {
        data: imports,
        meta: {
          total,
          page,
          perPage,
        },
      };
    },
  });
};
